import type { z } from 'zod';
import type {
  JsonObject,
  SceneConstructInvocation,
  SceneDefinition,
  SceneInteraction,
  SceneLayout,
  SceneNode,
  SceneRelation,
  SceneRepeat,
  SceneTimeline,
  SceneVisualState,
} from '../../contracts/index.ts';
import type { SceneCapabilityDescriptor } from './types.ts';
import { z as zod } from 'zod';
import { serializeZodSchema } from '../../catalog/ProtocolSchemaRegistry.ts';
import {
  sceneDefinitionSchema,
  sceneInteractionSchema,
  sceneLayoutSchema,
  sceneNodeSchema,
  sceneRelationSchema,
  sceneRepeatSchema,
  sceneVisualStateSchema,
} from '../../contracts/index.ts';

export interface SceneConstructExpansion {
  nodes: SceneNode[];
  definitions: SceneDefinition[];
  repeats: SceneRepeat[];
  layouts: SceneLayout[];
  relations: SceneRelation[];
  states: SceneVisualState[];
  timelines: SceneTimeline[];
  interactions: SceneInteraction[];
}

export interface VisualConstructDefinition {
  descriptor: SceneCapabilityDescriptor;
  schema: z.ZodTypeAny;
  expand: (
    invocation: SceneConstructInvocation,
    params: unknown,
  ) => SceneConstructExpansion;
}

function emptyExpansion(): SceneConstructExpansion {
  return {
    nodes: [],
    definitions: [],
    repeats: [],
    layouts: [],
    relations: [],
    states: [],
    timelines: [],
    interactions: [],
  };
}

export class VisualConstructRegistry {
  private readonly entries = new Map<string, VisualConstructDefinition>();

  register<TSchema extends z.ZodTypeAny>(definition: {
    descriptor: SceneCapabilityDescriptor;
    schema: TSchema;
    expand: (
      invocation: SceneConstructInvocation,
      params: z.output<TSchema>,
    ) => SceneConstructExpansion;
  }): this {
    if (!/^probfun\.scene\.construct\.[a-z0-9-]+@1$/.test(
      definition.descriptor.key,
    )) {
      throw new Error(`VisualConstruct key 非法：${definition.descriptor.key}`);
    }
    if (definition.descriptor.category !== 'construct')
      throw new Error('VisualConstruct Descriptor category 必须是 construct');
    if (this.entries.has(definition.descriptor.key))
      throw new Error(`VisualConstruct 已注册：${definition.descriptor.key}`);
    this.entries.set(definition.descriptor.key, {
      descriptor: definition.descriptor,
      schema: definition.schema,
      expand: (invocation, params) => definition.expand(
        invocation,
        definition.schema.parse(params),
      ),
    });
    return this;
  }

  resolve(key: string): VisualConstructDefinition {
    const definition = this.entries.get(key);
    if (!definition)
      throw new Error(`未知 VisualConstruct：${key}`);
    return definition;
  }

  expand(invocation: SceneConstructInvocation): SceneConstructExpansion {
    const definition = this.resolve(invocation.capability);
    return definition.expand(invocation, invocation.params);
  }

  listDescriptors(): SceneCapabilityDescriptor[] {
    return [...this.entries.values()].map(item =>
      structuredClone(item.descriptor),
    );
  }
}

function constructDescriptor(
  name: string,
  title: string,
  description: string,
  schema: z.ZodType<unknown>,
  suitableFor: string[],
  cost: SceneCapabilityDescriptor['cost'] = 'low',
  examples: JsonObject[] = [{}],
): SceneCapabilityDescriptor {
  const key = `probfun.scene.construct.${name}@1`;
  return {
    key,
    category: 'construct',
    title,
    description,
    suitableFor,
    inputs: ['结构化参数', '可选 Dataset/DataView 插槽'],
    outputs: ['基础 SceneGrammar 节点、布局和关系'],
    compatibleWith: ['probfun.scene-grammar@1'],
    configSchema: serializeZodSchema(schema, key),
    constraints: ['构造必须完全展开为基础语法', '禁止案例专用模型逻辑'],
    determinism: ['相同 invocation、参数和插槽展开为相同稳定编号。'],
    incrementalStrategy: ['展开后的稳定编号交给 SceneReconciler 局部更新。'],
    diagnostics: ['参数、插槽或展开引用错误定位到 construct invocation。'],
    cost,
    examples,
    counterExamples: [{ component: '<HospitalQueue />' }],
    commonErrors: ['参数不完整', '引用未知 DataView', '展开后编号冲突'],
    aiGuidance: ['构造用于复用视觉惯用结构，不表示具体案例。'],
    repairGuidance: ['根据结构化错误修正参数或改用基础语法。'],
  };
}

function groupNode(id: string, parentId?: string): SceneNode {
  return sceneNodeSchema.parse({
    id,
    ...(parentId ? { parentId } : {}),
    kind: 'group',
    semantic: { role: 'construct-root' },
  });
}

function rectNode(
  id: string,
  parentId: string,
  x: number,
  y: number,
  width: number,
  height: number,
  fill = '$surface',
  label = '',
  opacity = 1,
): SceneNode {
  return sceneNodeSchema.parse({
    id,
    parentId,
    kind: 'shape',
    transform: { x, y },
    geometry: { type: 'rect', width, height, radius: 6 },
    style: {
      fill,
      stroke: '$muted',
      strokeWidth: 1,
      opacity,
    },
    semantic: { label },
  });
}

function textNode(
  id: string,
  parentId: string,
  text: string,
  x: number,
  y: number,
): SceneNode {
  return sceneNodeSchema.parse({
    id,
    parentId,
    kind: 'text',
    text,
    transform: { x, y },
    textStyle: {
      color: '$text',
      fontSize: 14,
      fontWeight: '500',
      align: 'center',
    },
    anchorX: 0.5,
    anchorY: 0.5,
  });
}

function circleNode(
  id: string,
  parentId: string,
  cx: number,
  cy: number,
  radius: number,
  fill = '$surface',
  opacity = 1,
  label = '',
): SceneNode {
  return sceneNodeSchema.parse({
    id,
    parentId,
    kind: 'shape',
    transform: { x: cx, y: cy },
    geometry: { type: 'circle', radius },
    style: {
      fill,
      stroke: '$muted',
      strokeWidth: 1,
      opacity,
    },
    semantic: { label },
  });
}

function ringNode(
  id: string,
  parentId: string,
  cx: number,
  cy: number,
  radius: number,
  stroke = '$primary',
  label = '',
): SceneNode {
  return sceneNodeSchema.parse({
    id,
    parentId,
    kind: 'shape',
    transform: { x: cx, y: cy },
    geometry: { type: 'circle', radius },
    style: {
      fill: 'transparent',
      stroke,
      strokeWidth: 2,
      opacity: 1,
    },
    semantic: { label },
  });
}

export function createBuiltinVisualConstructRegistry():
VisualConstructRegistry {
  const registry = new VisualConstructRegistry();

  const bufferSchema = zod.object({
    capacity: zod.number().int().positive().max(1000).default(8),
    x: zod.number().finite().default(0),
    y: zod.number().finite().default(0),
    cellWidth: zod.number().positive().max(10000).default(52),
    cellHeight: zod.number().positive().max(10000).default(52),
    gap: zod.number().nonnegative().max(1000).default(8),
    dataViewId: zod.string().min(1).optional(),
    keyField: zod.string().min(1).default('id'),
    labelField: zod.string().min(1).default('label'),
  }).strict();
  registry.register({
    descriptor: constructDescriptor(
      'ordered-buffer',
      '有序缓冲',
      '表达队列、库存槽位、等待区或任何有序缓冲结构。',
      bufferSchema,
      ['排队', '库存', '流水线', '等待区'],
    ),
    schema: bufferSchema,
    expand: (invocation, params) => {
      const expansion = emptyExpansion();
      const rootId = `${invocation.id}:root`;
      expansion.nodes.push(groupNode(rootId, invocation.parentId));
      for (let index = 0; index < params.capacity; index += 1) {
        expansion.nodes.push(rectNode(
          `${invocation.id}:cell:${index}`,
          rootId,
          params.x + index * (params.cellWidth + params.gap),
          params.y,
          params.cellWidth,
          params.cellHeight,
          index % 2 === 0 ? '$surface' : '#f8fafc',
          `槽位 ${index + 1}`,
        ));
      }
      if (params.dataViewId) {
        const definitionId = `${invocation.id}:item-definition`;
        expansion.definitions.push(sceneDefinitionSchema.parse({
          id: definitionId,
          nodes: [{
            id: 'item',
            kind: 'group',
            semantic: { role: 'buffer-item' },
          }, {
            id: 'glyph',
            kind: 'shape',
            parentId: 'item',
            geometry: {
              type: 'circle',
              radius: Math.min(params.cellWidth, params.cellHeight) * 0.3,
            },
            style: {
              fill: '$primary',
              stroke: '#ffffff',
              strokeWidth: 2,
            },
          }, {
            id: 'label',
            kind: 'text',
            parentId: 'item',
            text: '',
            transform: { x: 0, y: 0 },
            textStyle: {
              color: '#ffffff',
              fontSize: 12,
              fontWeight: '600',
              align: 'center',
            },
            anchorX: 0.5,
            anchorY: 0.5,
          }],
          rootIds: ['item'],
        }));
        expansion.repeats.push(sceneRepeatSchema.parse({
          id: `${invocation.id}:items`,
          dataViewId: params.dataViewId,
          keyField: params.keyField,
          definitionId,
          parentId: rootId,
          mappings: {
            'label.text': {
              source: 'field',
              field: params.labelField,
              fallback: '',
            },
          },
        }));
        expansion.layouts.push(sceneLayoutSchema.parse({
          id: `${invocation.id}:item-layout`,
          capability: 'probfun.scene.layout.queue@1',
          targetPrefix: `${invocation.id}:items::`,
          config: {
            x: params.x + params.cellWidth / 2,
            y: params.y + params.cellHeight / 2,
            width: params.capacity * (params.cellWidth + params.gap),
            height: params.cellHeight,
            gap: params.gap,
          },
        }));
      }
      return expansion;
    },
  });

  const flowSchema = zod.object({
    sourceLabel: zod.string().min(1).max(200).default('来源'),
    targetLabel: zod.string().min(1).max(200).default('目标'),
    x: zod.number().finite().default(0),
    y: zod.number().finite().default(0),
    distance: zod.number().positive().max(100000).default(320),
  }).strict();
  registry.register({
    descriptor: constructDescriptor(
      'source-target-flow',
      '源到目标流动',
      '表达实体从来源经过路径到达目标的通用流动结构。',
      flowSchema,
      ['到达过程', '运输', '传播', '处理流程'],
    ),
    schema: flowSchema,
    expand: (invocation, params) => {
      const expansion = emptyExpansion();
      const rootId = `${invocation.id}:root`;
      const sourceId = `${invocation.id}:source`;
      const targetId = `${invocation.id}:target`;
      expansion.nodes.push(
        groupNode(rootId, invocation.parentId),
        rectNode(sourceId, rootId, params.x, params.y, 120, 72, '$secondary'),
        rectNode(
          targetId,
          rootId,
          params.x + params.distance,
          params.y,
          120,
          72,
          '$primary',
        ),
        textNode(
          `${invocation.id}:source-label`,
          rootId,
          params.sourceLabel,
          params.x + 60,
          params.y + 36,
        ),
        textNode(
          `${invocation.id}:target-label`,
          rootId,
          params.targetLabel,
          params.x + params.distance + 60,
          params.y + 36,
        ),
      );
      expansion.relations.push(sceneRelationSchema.parse({
        id: `${invocation.id}:flow`,
        type: 'connect',
        sourceId,
        targetId,
        sourceAnchor: 'east',
        targetAnchor: 'west',
        route: 'straight',
        markerEnd: 'arrow',
        style: {
          stroke: '$primary',
          strokeWidth: 3,
        },
      }));
      return expansion;
    },
  });

  const binsSchema = zod.object({
    binCount: zod.number().int().min(2).max(100).default(10),
    x: zod.number().finite().default(0),
    y: zod.number().finite().default(0),
    width: zod.number().positive().max(100000).default(600),
    height: zod.number().positive().max(100000).default(280),
  }).strict();
  registry.register({
    descriptor: constructDescriptor(
      'sample-bins',
      '样本进入分箱',
      '表达样本落入离散区间和累计高度的视觉结构。',
      binsSchema,
      ['直方图形成', '抽样', '分布聚合'],
    ),
    schema: binsSchema,
    expand: (invocation, params) => {
      const expansion = emptyExpansion();
      const rootId = `${invocation.id}:root`;
      const binWidth = params.width / params.binCount;
      expansion.nodes.push(groupNode(rootId, invocation.parentId));
      for (let index = 0; index < params.binCount; index += 1) {
        expansion.nodes.push(rectNode(
          `${invocation.id}:bin:${index}`,
          rootId,
          params.x + index * binWidth,
          params.y,
          Math.max(binWidth - 2, 1),
          params.height,
          index % 2 === 0 ? '#dbeafe' : '#e0f2fe',
          `区间 ${index + 1}`,
        ));
      }
      return expansion;
    },
  });

  const networkSchema = zod.object({
    nodes: zod.array(zod.object({
      id: zod.string().min(1),
      label: zod.string().max(200).default(''),
    }).strict()).min(1).max(500),
    edges: zod.array(zod.object({
      id: zod.string().min(1),
      source: zod.string().min(1),
      target: zod.string().min(1),
      label: zod.string().max(200).default(''),
    }).strict()).max(5000).default([]),
    x: zod.number().finite().default(0),
    y: zod.number().finite().default(0),
    width: zod.number().positive().max(100000).default(640),
    height: zod.number().positive().max(100000).default(360),
  }).strict();
  registry.register({
    descriptor: constructDescriptor(
      'state-network',
      '状态转移网络',
      '表达有限状态、转移、网络传播和流程关系。',
      networkSchema,
      ['状态机', '马尔可夫结构', '传播网络', '流程图'],
      'high',
      [{
        nodes: [{ id: 'state-a', label: '状态 A' }],
        edges: [],
      }],
    ),
    schema: networkSchema,
    expand: (invocation, params) => {
      const expansion = emptyExpansion();
      const rootId = `${invocation.id}:root`;
      expansion.nodes.push(groupNode(rootId, invocation.parentId));
      for (const [index, node] of params.nodes.entries()) {
        const nodeId = `${invocation.id}:node:${node.id}`;
        expansion.nodes.push(
          sceneNodeSchema.parse({
            id: nodeId,
            parentId: rootId,
            kind: 'shape',
            geometry: { type: 'circle', radius: 32 },
            style: {
              fill: index === 0 ? '$primary' : '$surface',
              stroke: '$primary',
              strokeWidth: 2,
            },
            semantic: { label: node.label, role: 'state' },
          }),
          textNode(
            `${invocation.id}:label:${node.id}`,
            rootId,
            node.label || node.id,
            0,
            0,
          ),
        );
        expansion.relations.push(sceneRelationSchema.parse({
          id: `${invocation.id}:label-attach:${node.id}`,
          type: 'attach-label',
          sourceId: nodeId,
          targetId: `${invocation.id}:label:${node.id}`,
          sourceAnchor: 'center',
          targetAnchor: 'center',
        }));
      }
      expansion.layouts.push(sceneLayoutSchema.parse({
        id: `${invocation.id}:layout`,
        capability: 'probfun.scene.layout.force@1',
        targetPrefix: `${invocation.id}:node:`,
        config: {
          x: params.x,
          y: params.y,
          width: params.width,
          height: params.height,
          iterations: 100,
        },
      }));
      for (const edge of params.edges) {
        expansion.relations.push(sceneRelationSchema.parse({
          id: `${invocation.id}:edge:${edge.id}`,
          type: 'connect',
          sourceId: `${invocation.id}:node:${edge.source}`,
          targetId: `${invocation.id}:node:${edge.target}`,
          route: 'curve',
          markerEnd: 'arrow',
          label: edge.label,
          style: { stroke: '$muted', strokeWidth: 2 },
        }));
      }
      return expansion;
    },
  });

  const pipelineSchema = zod.object({
    stages: zod.array(zod.object({
      id: zod.string().min(1),
      label: zod.string().min(1).max(200),
    }).strict()).min(2).max(100),
    x: zod.number().finite().default(0),
    y: zod.number().finite().default(0),
    stageWidth: zod.number().positive().max(10000).default(140),
    stageHeight: zod.number().positive().max(10000).default(80),
    gap: zod.number().nonnegative().max(10000).default(80),
  }).strict();
  registry.register({
    descriptor: constructDescriptor(
      'process-pipeline',
      '多阶段流程管线',
      '表达顺序处理、分阶段教学叙事和状态推进。',
      pipelineSchema,
      ['处理流程', '教学步骤', '多阶段系统'],
      'low',
      [{
        stages: [
          { id: 'input', label: '输入' },
          { id: 'output', label: '输出' },
        ],
      }],
    ),
    schema: pipelineSchema,
    expand: (invocation, params) => {
      const expansion = emptyExpansion();
      const rootId = `${invocation.id}:root`;
      expansion.nodes.push(groupNode(rootId, invocation.parentId));
      params.stages.forEach((stage, index) => {
        const stageId = `${invocation.id}:stage:${stage.id}`;
        expansion.nodes.push(
          rectNode(
            stageId,
            rootId,
            params.x + index * (params.stageWidth + params.gap),
            params.y,
            params.stageWidth,
            params.stageHeight,
            index === 0 ? '#dbeafe' : '$surface',
            stage.label,
          ),
          textNode(
            `${invocation.id}:label:${stage.id}`,
            rootId,
            stage.label,
            params.x + params.stageWidth / 2
            + index * (params.stageWidth + params.gap),
            params.y + params.stageHeight / 2,
          ),
        );
        if (index > 0) {
          expansion.relations.push(sceneRelationSchema.parse({
            id: `${invocation.id}:transition:${index}`,
            type: 'connect',
            sourceId: `${invocation.id}:stage:${params.stages[index - 1]!.id}`,
            targetId: stageId,
            sourceAnchor: 'east',
            targetAnchor: 'west',
            markerEnd: 'arrow',
            style: { stroke: '$primary', strokeWidth: 2 },
          }));
        }
      });
      return expansion;
    },
  });

  const splitSchema = zod.object({
    leftLabel: zod.string().min(1).max(500),
    rightLabel: zod.string().min(1).max(500),
    x: zod.number().finite().default(0),
    y: zod.number().finite().default(0),
    width: zod.number().positive().max(100000).default(600),
    height: zod.number().positive().max(100000).default(280),
  }).strict();
  registry.register({
    descriptor: constructDescriptor(
      'split-compare',
      '分裂比较',
      '把一个问题或群体分成两个并列区域进行对照。',
      splitSchema,
      ['条件比较', '策略比较', '表示变换'],
      'low',
      [{ leftLabel: '方案 A', rightLabel: '方案 B' }],
    ),
    schema: splitSchema,
    expand: (invocation, params) => {
      const expansion = emptyExpansion();
      const rootId = `${invocation.id}:root`;
      const half = params.width / 2;
      const paneWidth = half - 8;
      const leftX = params.x;
      const rightX = params.x + half + 8;
      const labelY = params.y + params.height / 2;
      expansion.nodes.push(
        groupNode(rootId, invocation.parentId),
        rectNode(
          `${invocation.id}:left`,
          rootId,
          leftX,
          params.y,
          paneWidth,
          params.height,
          '#dbeafe',
          params.leftLabel,
          0.42,
        ),
        rectNode(
          `${invocation.id}:right`,
          rootId,
          rightX,
          params.y,
          paneWidth,
          params.height,
          '#ecfeff',
          params.rightLabel,
          0.42,
        ),
        textNode(
          `${invocation.id}:left-label`,
          rootId,
          params.leftLabel,
          leftX + paneWidth / 2,
          labelY,
        ),
        textNode(
          `${invocation.id}:right-label`,
          rootId,
          params.rightLabel,
          rightX + paneWidth / 2,
          labelY,
        ),
      );
      return expansion;
    },
  });

  const traceSchema = zod.object({
    points: zod.array(zod.object({
      x: zod.number().finite(),
      y: zod.number().finite(),
    }).strict()).min(2).max(100000),
    stroke: zod.string().default('$primary'),
    strokeWidth: zod.number().positive().max(100).default(3),
  }).strict();
  registry.register({
    descriptor: constructDescriptor(
      'path-trace',
      '路径追踪',
      '表达轨迹、累计路径和对象运动参考线。',
      traceSchema,
      ['随机游走', '运动轨迹', '过程路径'],
      'low',
      [{ points: [{ x: 0, y: 0 }, { x: 100, y: 60 }] }],
    ),
    schema: traceSchema,
    expand: (invocation, params) => {
      const expansion = emptyExpansion();
      expansion.nodes.push(sceneNodeSchema.parse({
        id: `${invocation.id}:trace`,
        ...(invocation.parentId ? { parentId: invocation.parentId } : {}),
        kind: 'shape',
        geometry: {
          type: 'polyline',
          points: params.points,
          closed: false,
        },
        style: {
          stroke: params.stroke,
          strokeWidth: params.strokeWidth,
          fill: 'transparent',
        },
        semantic: { role: 'trajectory' },
      }));
      return expansion;
    },
  });

  const cumulativeRegionSchema = zod.object({
    points: zod.array(zod.object({
      x: zod.number().finite(),
      y: zod.number().finite(),
    }).strict()).min(2).max(100000),
    baselineY: zod.number().finite().default(0),
    fill: zod.string().default('$primary'),
    opacity: zod.number().min(0).max(1).default(0.24),
  }).strict();
  registry.register({
    descriptor: constructDescriptor(
      'cumulative-region',
      '累积区域',
      '把折线与基线围合为面积，用于累计量、命中区域或概率质量的场景表达。',
      cumulativeRegionSchema,
      ['累计量', '区域命中', '面积比较', '过程包络'],
      'low',
      [{
        points: [{ x: 0, y: 80 }, { x: 100, y: 30 }],
        baselineY: 100,
      }],
    ),
    schema: cumulativeRegionSchema,
    expand: (invocation, params) => {
      const first = params.points[0]!;
      const last = params.points.at(-1)!;
      return {
        ...emptyExpansion(),
        nodes: [sceneNodeSchema.parse({
          id: `${invocation.id}:region`,
          ...(invocation.parentId ? { parentId: invocation.parentId } : {}),
          kind: 'shape',
          geometry: {
            type: 'polygon',
            points: [
              ...params.points,
              { x: last.x, y: params.baselineY },
              { x: first.x, y: params.baselineY },
            ],
          },
          style: {
            fill: params.fill,
            opacity: params.opacity,
            stroke: params.fill,
            strokeWidth: 2,
          },
          semantic: { role: 'cumulative-region' },
        })],
      };
    },
  });

  const branchMergeSchema = zod.object({
    branches: zod.array(zod.object({
      id: zod.string().min(1).max(100),
      label: zod.string().min(1).max(200),
    }).strict()).min(2).max(12),
    sourceLabel: zod.string().min(1).max(200).default('起点'),
    targetLabel: zod.string().min(1).max(200).default('汇合'),
    x: zod.number().finite().default(0),
    y: zod.number().finite().default(0),
    width: zod.number().positive().max(100000).default(640),
    gap: zod.number().positive().max(10000).default(76),
  }).strict();
  registry.register({
    descriptor: constructDescriptor(
      'branch-merge',
      '分支与汇合',
      '表达一个来源分成多个路径并在目标重新汇合的通用结构。',
      branchMergeSchema,
      ['分支过程', '条件路径', '策略比较', '传播路径'],
      'low',
      [{
        branches: [
          { id: 'branch-a', label: '路径 A' },
          { id: 'branch-b', label: '路径 B' },
        ],
      }],
    ),
    schema: branchMergeSchema,
    expand: (invocation, params) => {
      const expansion = emptyExpansion();
      const rootId = `${invocation.id}:root`;
      const sourceId = `${invocation.id}:source`;
      const targetId = `${invocation.id}:target`;
      const centerY = params.y + (params.branches.length - 1) * params.gap / 2;
      expansion.nodes.push(
        groupNode(rootId, invocation.parentId),
        rectNode(sourceId, rootId, params.x, centerY - 28, 100, 56, '$secondary'),
        textNode(
          `${invocation.id}:source-label`,
          rootId,
          params.sourceLabel,
          params.x + 50,
          centerY,
        ),
        rectNode(
          targetId,
          rootId,
          params.x + params.width - 100,
          centerY - 28,
          100,
          56,
          '$primary',
        ),
        textNode(
          `${invocation.id}:target-label`,
          rootId,
          params.targetLabel,
          params.x + params.width - 50,
          centerY,
        ),
      );
      params.branches.forEach((branch, index) => {
        const branchId = `${invocation.id}:branch:${branch.id}`;
        const branchY = params.y + index * params.gap;
        expansion.nodes.push(
          rectNode(
            branchId,
            rootId,
            params.x + params.width / 2 - 60,
            branchY - 24,
            120,
            48,
            '$surface',
            branch.label,
          ),
          textNode(
            `${invocation.id}:branch-label:${branch.id}`,
            rootId,
            branch.label,
            params.x + params.width / 2,
            branchY,
          ),
        );
        for (const [suffix, from, to] of [
          ['out', sourceId, branchId],
          ['in', branchId, targetId],
        ] as const) {
          expansion.relations.push(sceneRelationSchema.parse({
            id: `${invocation.id}:${suffix}:${branch.id}`,
            type: 'connect',
            sourceId: from,
            targetId: to,
            sourceAnchor: 'east',
            targetAnchor: 'west',
            route: 'curve',
            markerEnd: 'arrow',
            style: { stroke: '$muted', strokeWidth: 2 },
          }));
        }
      });
      return expansion;
    },
  });

  const movingWindowSchema = zod.object({
    x: zod.number().finite().default(0),
    y: zod.number().finite().default(0),
    domainWidth: zod.number().positive().max(100000).default(600),
    height: zod.number().positive().max(100000).default(160),
    windowStart: zod.number().nonnegative().default(0),
    windowWidth: zod.number().positive().max(100000).default(120),
    label: zod.string().max(200).default('观察窗口'),
  }).strict();
  registry.register({
    descriptor: constructDescriptor(
      'moving-window',
      '移动观察窗口',
      '在连续区域上标记可移动的局部观察区，用于滑动统计与过程截面。',
      movingWindowSchema,
      ['滑动统计', '时间窗', '局部观察', '过程截面'],
    ),
    schema: movingWindowSchema,
    expand: (invocation, params) => {
      const rootId = `${invocation.id}:root`;
      return {
        ...emptyExpansion(),
        nodes: [
          groupNode(rootId, invocation.parentId),
          rectNode(
            `${invocation.id}:domain`,
            rootId,
            params.x,
            params.y,
            params.domainWidth,
            params.height,
            '#f8fafc',
            '观察域',
          ),
          rectNode(
            `${invocation.id}:window`,
            rootId,
            params.x + params.windowStart,
            params.y,
            Math.min(params.windowWidth, params.domainWidth),
            params.height,
            '#dbeafe',
            params.label,
          ),
          textNode(
            `${invocation.id}:label`,
            rootId,
            params.label,
            params.x + params.windowStart
            + Math.min(params.windowWidth, params.domainWidth) / 2,
            params.y + 20,
          ),
        ],
      };
    },
  });

  const revealSchema = zod.object({
    triggerLabel: zod.string().min(1).max(200).default('查看提示'),
    revealLabel: zod.string().min(1).max(1000),
    x: zod.number().finite().default(0),
    y: zod.number().finite().default(0),
    width: zod.number().positive().max(100000).default(360),
    height: zod.number().positive().max(100000).default(120),
  }).strict();
  registry.register({
    descriptor: constructDescriptor(
      'conditional-reveal',
      '条件揭示',
      '通过声明式状态与点击交互逐步揭示提示、证据或结论。',
      revealSchema,
      ['教学提示', '逐步解释', '证据揭示', '结论确认'],
      'low',
      [{ revealLabel: '新的证据或解释' }],
    ),
    schema: revealSchema,
    expand: (invocation, params) => {
      const expansion = emptyExpansion();
      const rootId = `${invocation.id}:root`;
      const triggerId = `${invocation.id}:trigger`;
      const panelId = `${invocation.id}:panel`;
      const stateId = `${invocation.id}:revealed`;
      const interactionId = `${invocation.id}:toggle`;
      expansion.nodes.push(
        groupNode(rootId, invocation.parentId),
        sceneNodeSchema.parse({
          ...rectNode(
            triggerId,
            rootId,
            params.x,
            params.y,
            120,
            40,
            '$primary',
            params.triggerLabel,
          ),
          interactionIds: [interactionId],
          accessibility: {
            label: params.triggerLabel,
            focusable: true,
          },
        }),
        textNode(
          `${invocation.id}:trigger-label`,
          rootId,
          params.triggerLabel,
          params.x + 60,
          params.y + 20,
        ),
        sceneNodeSchema.parse({
          ...rectNode(
            panelId,
            rootId,
            params.x,
            params.y + 56,
            params.width,
            params.height,
            '#eff6ff',
            params.revealLabel,
          ),
          visible: false,
        }),
        sceneNodeSchema.parse({
          ...textNode(
            `${invocation.id}:panel-label`,
            rootId,
            params.revealLabel,
            params.x + params.width / 2,
            params.y + 56 + params.height / 2,
          ),
          visible: false,
        }),
      );
      expansion.states.push(sceneVisualStateSchema.parse({
        id: stateId,
        source: 'custom',
        priority: 100,
        visible: true,
      }));
      expansion.interactions.push(sceneInteractionSchema.parse({
        id: interactionId,
        event: 'pointertap',
        cursor: 'pointer',
        actions: [{
          type: 'state',
          stateId,
          mode: 'toggle',
          targetIds: [
            panelId,
            `${invocation.id}:panel-label`,
          ],
        }],
      }));
      return expansion;
    },
  });

  const transformSchema = zod.object({
    fromLabel: zod.string().min(1).max(200),
    toLabel: zod.string().min(1).max(200),
    x: zod.number().finite().default(0),
    y: zod.number().finite().default(0),
    distance: zod.number().positive().max(100000).default(360),
  }).strict();
  registry.register({
    descriptor: constructDescriptor(
      'representation-transform',
      '表示变换',
      '把同一对象或信息的两种表示并列，并显式标出变换方向。',
      transformSchema,
      ['表示切换', '模型到图形', '参数到结果', '抽象到具象'],
      'low',
      [{ fromLabel: '原表示', toLabel: '目标表示' }],
    ),
    schema: transformSchema,
    expand: (invocation, params) => {
      const expansion = emptyExpansion();
      const rootId = `${invocation.id}:root`;
      const fromId = `${invocation.id}:from`;
      const toId = `${invocation.id}:to`;
      expansion.nodes.push(
        groupNode(rootId, invocation.parentId),
        rectNode(fromId, rootId, params.x, params.y, 160, 96, '#f8fafc'),
        textNode(
          `${invocation.id}:from-label`,
          rootId,
          params.fromLabel,
          params.x + 80,
          params.y + 48,
        ),
        rectNode(
          toId,
          rootId,
          params.x + params.distance,
          params.y,
          160,
          96,
          '#eff6ff',
        ),
        textNode(
          `${invocation.id}:to-label`,
          rootId,
          params.toLabel,
          params.x + params.distance + 80,
          params.y + 48,
        ),
      );
      expansion.relations.push(sceneRelationSchema.parse({
        id: `${invocation.id}:transform`,
        type: 'connect',
        sourceId: fromId,
        targetId: toId,
        sourceAnchor: 'east',
        targetAnchor: 'west',
        markerEnd: 'arrow',
        label: '变换',
        style: { stroke: '$primary', strokeWidth: 3 },
      }));
      return expansion;
    },
  });

  const linkedRegionSchema = zod.object({
    sceneLabel: zod.string().min(1).max(200).default('对象区域'),
    summaryLabel: zod.string().min(1).max(200).default('观察摘要'),
    x: zod.number().finite().default(0),
    y: zod.number().finite().default(0),
    width: zod.number().positive().max(100000).default(560),
    height: zod.number().positive().max(100000).default(240),
  }).strict();
  registry.register({
    descriptor: constructDescriptor(
      'linked-observation-region',
      '对象与观察区域联动',
      '在同一二维场景中建立对象区与统计观察区的明确视觉关联。',
      linkedRegionSchema,
      ['对象与统计量联动', '局部与汇总', '证据关联'],
    ),
    schema: linkedRegionSchema,
    expand: (invocation, params) => {
      const expansion = emptyExpansion();
      const rootId = `${invocation.id}:root`;
      const sceneId = `${invocation.id}:scene-region`;
      const summaryId = `${invocation.id}:summary-region`;
      expansion.nodes.push(
        groupNode(rootId, invocation.parentId),
        rectNode(
          sceneId,
          rootId,
          params.x,
          params.y,
          params.width * 0.58,
          params.height,
          '#f8fafc',
          params.sceneLabel,
        ),
        rectNode(
          summaryId,
          rootId,
          params.x + params.width * 0.66,
          params.y,
          params.width * 0.34,
          params.height,
          '#eff6ff',
          params.summaryLabel,
        ),
        textNode(
          `${invocation.id}:scene-label`,
          rootId,
          params.sceneLabel,
          params.x + params.width * 0.29,
          params.y + 22,
        ),
        textNode(
          `${invocation.id}:summary-label`,
          rootId,
          params.summaryLabel,
          params.x + params.width * 0.83,
          params.y + 22,
        ),
      );
      expansion.relations.push(sceneRelationSchema.parse({
        id: `${invocation.id}:link`,
        type: 'associate',
        sourceId: sceneId,
        targetId: summaryId,
        sourceAnchor: 'east',
        targetAnchor: 'west',
        route: 'curve',
        markerEnd: 'arrow',
        style: {
          stroke: '$secondary',
          strokeWidth: 2,
          dash: [6, 4],
        },
      }));
      return expansion;
    },
  });

  const populationSchema = zod.object({
    dataViewId: zod.string().min(1),
    keyField: zod.string().min(1).default('id'),
    xField: zod.string().min(1).default('x'),
    yField: zod.string().min(1).default('y'),
    stateField: zod.string().min(1).default('state'),
    radius: zod.number().positive().max(1000).default(6),
  }).strict();
  registry.register({
    descriptor: constructDescriptor(
      'population-field',
      '群体生成与消亡',
      '由 DataView 稳定实例化大量空间实体。',
      populationSchema,
      ['粒子', '人群', '随机点', '传播过程'],
      'medium',
      [{ dataViewId: 'entities' }],
    ),
    schema: populationSchema,
    expand: (invocation, params) => {
      const expansion = emptyExpansion();
      const definitionId = `${invocation.id}:entity-definition`;
      expansion.definitions.push(sceneDefinitionSchema.parse({
        id: definitionId,
        nodes: [{
          id: 'entity',
          kind: 'shape',
          geometry: { type: 'circle', radius: params.radius },
          style: {
            fill: '$primary',
            stroke: '#ffffff',
            strokeWidth: 1,
          },
          semantic: { role: 'population-entity' },
        }],
        rootIds: ['entity'],
      }));
      expansion.repeats.push(sceneRepeatSchema.parse({
        id: `${invocation.id}:entities`,
        dataViewId: params.dataViewId,
        keyField: params.keyField,
        definitionId,
        ...(invocation.parentId ? { parentId: invocation.parentId } : {}),
        mappings: {
          'entity.transform.x': {
            source: 'field',
            field: params.xField,
            fallback: 0,
          },
          'entity.transform.y': {
            source: 'field',
            field: params.yField,
            fallback: 0,
          },
          'entity.semantic.role': {
            source: 'field',
            field: params.stateField,
            fallback: 'default',
          },
        },
      }));
      return expansion;
    },
  });

  const vennSchema = zod.object({
    x: zod.number().finite().default(0),
    y: zod.number().finite().default(0),
    width: zod.number().positive().max(100000).default(600),
    height: zod.number().positive().max(100000).default(400),
    sets: zod.array(zod.object({
      id: zod.string().min(1).max(200),
      label: zod.string().max(200).default(''),
      color: zod.string().max(64).optional(),
      elements: zod.array(zod.string().min(1).max(200)).max(2000).default([]),
    }).strict()).min(2).max(3),
    showLabels: zod.boolean().default(true),
    elementRadius: zod.number().positive().max(100).default(5),
    opacity: zod.number().min(0).max(1).default(0.45),
  }).strict();
  registry.register({
    descriptor: constructDescriptor(
      'venn',
      '集合/韦恩图',
      '表达事件关系、集合运算、互斥与独立；支持 2 或 3 个集合及元素点归属。',
      vennSchema,
      ['事件关系', '条件概率', '集合运算', '互斥与独立'],
      'medium',
      [{ sets: [{ id: 'a', label: '事件 A' }, { id: 'b', label: '事件 B' }] }],
    ),
    schema: vennSchema,
    expand: (invocation, params) => {
      const expansion = emptyExpansion();
      const rootId = `${invocation.id}:root`;
      const setCount = params.sets.length;
      const cx = params.x + params.width / 2;
      const cy = params.y + params.height / 2;
      const size = Math.min(params.width, params.height);
      const radius = setCount === 2
        ? size * 0.42
        : size * 0.36;
      const centerDistance = setCount === 2
        ? size * 0.22
        : size * 0.42;

      const centers: Array<{ x: number; y: number }> = setCount === 2
        ? [
            { x: cx - centerDistance, y: cy },
            { x: cx + centerDistance, y: cy },
          ]
        : [
            { x: cx, y: cy - centerDistance },
            { x: cx - centerDistance, y: cy + centerDistance },
            { x: cx + centerDistance, y: cy + centerDistance },
          ];

      expansion.nodes.push(groupNode(rootId, invocation.parentId));

      // 集合区域：半透明填充 + 描边环
      const defaultColors = ['#dbeafe', '#c7d2fe', '#cffafe'];
      params.sets.forEach((set, index) => {
        const center = centers[index]!;
        const color = set.color ?? defaultColors[index % defaultColors.length]!;
        expansion.nodes.push(
          circleNode(
            `${invocation.id}:set:${set.id}`,
            rootId,
            center.x,
            center.y,
            radius,
            color,
            params.opacity,
            set.label || set.id,
          ),
          ringNode(
            `${invocation.id}:ring:${set.id}`,
            rootId,
            center.x,
            center.y,
            radius,
            color,
            set.label || set.id,
          ),
        );
        if (params.showLabels) {
          expansion.nodes.push(textNode(
            `${invocation.id}:set-label:${set.id}`,
            rootId,
            set.label || set.id,
            center.x,
            center.y - radius - 14,
          ));
        }
      });

      // 元素点：按集合归属放入对应区域（2 集场景采用确定性归属）
      const allElements = params.sets.flatMap((set, setIndex) =>
        set.elements.map(element => ({ element, setIndex })),
      );
      allElements.forEach((entry, elementIndex) => {
        let placeX = cx;
        let placeY = cy;
        if (setCount === 2) {
          const inA = entry.setIndex === 0;
          const inB = entry.setIndex === 1;
          if (inA && inB) {
            placeX = cx;
          }
          else if (inA) {
            placeX = cx - centerDistance * 0.9;
          }
          else {
            placeX = cx + centerDistance * 0.9;
          }
        }
        else {
          const center = centers[entry.setIndex % centers.length]!;
          placeX = center.x;
          placeY = center.y;
        }
        // 确定性散布：基于元素 index 的黄金角，避免全部重叠
        const angle = elementIndex * 2.399963229728653;
        const spread = elementIndex % 3 === 0
          ? radius * 0.12
          : radius * (0.18 + (elementIndex % 4) * 0.05);
        const dotX = placeX + Math.cos(angle) * spread;
        const dotY = placeY + Math.sin(angle) * spread;
        expansion.nodes.push(circleNode(
          `${invocation.id}:element:${entry.element}`,
          rootId,
          dotX,
          dotY,
          params.elementRadius,
          '#334155',
          0.9,
          entry.element,
        ));
      });

      return expansion;
    },
  });

  interface ProbabilityBranchInput {
    id: string;
    label: string;
    probability: number;
    children?: ProbabilityBranchInput[];
  }
  const probabilityBranchSchema: zod.ZodType<ProbabilityBranchInput> = zod.lazy(
    () => zod.object({
      id: zod.string().min(1).max(200),
      label: zod.string().min(1).max(200),
      probability: zod.number().min(0).max(1),
      children: zod.array(probabilityBranchSchema).max(100).default([]),
    }).strict(),
  );
  const probabilityTreeSchema = zod.object({
    x: zod.number().finite().default(0),
    y: zod.number().finite().default(0),
    width: zod.number().positive().max(100000).default(640),
    height: zod.number().positive().max(100000).default(360),
    rootLabel: zod.string().min(1).max(200).default('起点'),
    branches: zod.array(probabilityBranchSchema).min(1).max(100),
    showProbability: zod.boolean().default(true),
    leafLabel: zod.string().max(200).default(''),
    nodeRadius: zod.number().positive().max(200).default(24),
  }).strict();
  registry.register({
    descriptor: constructDescriptor(
      'probability-tree',
      '概率树/事件树',
      '表达贝叶斯更新、序列实验和分支概率；树节点带条件概率边标签。',
      probabilityTreeSchema,
      ['贝叶斯更新', '序列实验', '分支概率', '条件概率'],
      'medium',
      [{
        rootLabel: '起点',
        branches: [{ id: 'a', label: '分支 A', probability: 0.5 }],
      }],
    ),
    schema: probabilityTreeSchema,
    expand: (invocation, params) => {
      const expansion = emptyExpansion();
      const rootId = `${invocation.id}:root`;
      const levelWidth = params.width / (maxTreeDepth(params.branches) + 1);
      const leafCount = countTreeLeaves(params.branches);
      const leafCounter = { value: 0 };
      const positions: Array<{
        id: string;
        path: string[];
        label: string;
        probability?: number;
        depth: number;
        isLeaf: boolean;
        x: number;
        y: number;
      }> = [];

      layoutTree(
        params.branches,
        1,
        params,
        levelWidth,
        leafCount,
        leafCounter,
        positions,
      );

      expansion.nodes.push(groupNode(rootId, invocation.parentId));

      // 根节点（起点）
      const rootX = params.x + 0 * levelWidth;
      const rootY = params.y + params.height / 2;
      expansion.nodes.push(
        circleNode(
          `${invocation.id}:node:root`,
          rootId,
          rootX,
          rootY,
          params.nodeRadius,
          '$primary',
          1,
          params.rootLabel,
        ),
      );
      expansion.nodes.push(textNode(
        `${invocation.id}:root-label`,
        rootId,
        params.rootLabel,
        rootX,
        rootY - params.nodeRadius - 12,
      ));

      const nodeId = (path: string[]): string =>
        `${invocation.id}:node:${path.join('-')}`;

      for (const pos of positions) {
        const id = nodeId(pos.path);
        expansion.nodes.push(
          circleNode(
            id,
            rootId,
            pos.x,
            pos.y,
            params.nodeRadius,
            pos.isLeaf ? '#e0f2fe' : '$surface',
            1,
            pos.label,
          ),
        );
        expansion.nodes.push(textNode(
          `${id}:label`,
          rootId,
          pos.label,
          pos.x,
          pos.y + params.nodeRadius + 12,
        ));
        if (pos.isLeaf && params.leafLabel) {
          expansion.nodes.push(textNode(
            `${id}:leaf-label`,
            rootId,
            params.leafLabel,
            pos.x,
            pos.y - params.nodeRadius - 12,
          ));
        }
        // 父边
        if (pos.path.length > 1) {
          const parentId = nodeId(pos.path.slice(0, -1));
          expansion.relations.push(sceneRelationSchema.parse({
            id: `${invocation.id}:edge:${pos.path.join('-')}`,
            type: 'connect',
            sourceId: parentId,
            targetId: id,
            route: 'orthogonal',
            markerEnd: 'none',
            label: params.showProbability && pos.probability !== undefined
              ? String(pos.probability)
              : '',
            style: { stroke: '$muted', strokeWidth: 2 },
          }));
        }
        else {
          expansion.relations.push(sceneRelationSchema.parse({
            id: `${invocation.id}:edge:${pos.path.join('-')}`,
            type: 'connect',
            sourceId: `${invocation.id}:node:root`,
            targetId: id,
            route: 'orthogonal',
            markerEnd: 'none',
            label: params.showProbability && pos.probability !== undefined
              ? String(pos.probability)
              : '',
            style: { stroke: '$muted', strokeWidth: 2 },
          }));
        }
      }

      return expansion;
    },
  });

  function maxTreeDepth(branches: ProbabilityBranchInput[]): number {
    let depth = 0;
    for (const branch of branches) {
      const children = branch.children ?? [];
      const childDepth = children.length > 0
        ? 1 + maxTreeDepth(children)
        : 1;
      depth = Math.max(depth, childDepth);
    }
    return depth;
  }

  function countTreeLeaves(branches: ProbabilityBranchInput[]): number {
    let count = 0;
    for (const branch of branches) {
      const children = branch.children ?? [];
      count += children.length > 0
        ? countTreeLeaves(children)
        : 1;
    }
    return Math.max(count, 1);
  }

  function layoutTree(
    branches: ProbabilityBranchInput[],
    depth: number,
    params: zod.infer<typeof probabilityTreeSchema>,
    levelWidth: number,
    leafCount: number,
    leafCounter: { value: number },
    out: Array<{
      id: string;
      path: string[];
      label: string;
      probability?: number;
      depth: number;
      isLeaf: boolean;
      x: number;
      y: number;
    }>,
    parentPath: string[] = [],
  ): void {
    for (const branch of branches) {
      const path = [...parentPath, branch.id];
      const children = branch.children ?? [];
      const isLeaf = children.length === 0;
      const x = params.x + depth * levelWidth;
      let y: number;
      if (isLeaf) {
        y = params.y + (leafCounter.value + 0.5) * (params.height / leafCount);
        leafCounter.value += 1;
      }
      else {
        layoutTree(
          children,
          depth + 1,
          params,
          levelWidth,
          leafCount,
          leafCounter,
          out,
          path,
        );
        const childEntries = out.filter(entry =>
          entry.path.length > path.length
          && entry.path.slice(0, path.length).join('-') === path.join('-'),
        );
        const childTotal = childEntries.reduce(
          (sum, entry) => sum + entry.y,
          0,
        );
        const childY = childEntries.length > 0
          ? childTotal / childEntries.length
          : params.y + params.height / 2;
        y = childY;
      }
      out.push({
        id: branch.id,
        path,
        label: branch.label,
        probability: branch.probability,
        depth,
        isLeaf,
        x,
        y,
      });
    }
  }

  const densityGridSchema = zod.object({
    dataViewId: zod.string().min(1),
    keyField: zod.string().min(1).default('id'),
    xField: zod.string().min(1).default('x'),
    yField: zod.string().min(1).default('y'),
    valueField: zod.string().min(1).default('count'),
    x: zod.number().finite().default(0),
    y: zod.number().finite().default(0),
    width: zod.number().positive().max(100000).default(600),
    height: zod.number().positive().max(100000).default(400),
    columns: zod.number().int().min(1).max(200).default(20),
    rows: zod.number().int().min(1).max(200).default(20),
    colorScaleId: zod.string().min(1).optional(),
    xScaleId: zod.string().min(1).optional(),
    yScaleId: zod.string().min(1).optional(),
    lowColor: zod.string().max(64).optional(),
    highColor: zod.string().max(64).optional(),
    showAxes: zod.boolean().default(true),
  }).strict();
  registry.register({
    descriptor: constructDescriptor(
      'density-grid',
      '热力密度网格',
      '把已分箱的二维数据渲染为按密度着色的网格，用于二维分布与密度直观。',
      densityGridSchema,
      ['二维分布', '密度场', '二维联合分布', '热力图'],
      'medium',
      [{ dataViewId: 'bins', columns: 20, rows: 20 }],
    ),
    schema: densityGridSchema,
    expand: (invocation, params) => {
      const expansion = emptyExpansion();
      const rootId = `${invocation.id}:root`;
      const cellWidth = params.width / params.columns;
      const cellHeight = params.height / params.rows;
      const lowColor = params.lowColor ?? '#eff6ff';
      const highColor = params.highColor ?? '#2563eb';
      const definitionId = `${invocation.id}:cell-def`;

      expansion.nodes.push(groupNode(rootId, invocation.parentId));

      expansion.definitions.push(sceneDefinitionSchema.parse({
        id: definitionId,
        rootIds: ['cell'],
        nodes: [{
          id: 'cell',
          kind: 'group',
          semantic: { role: 'density-cell' },
        }, {
          id: 'cell-rect',
          parentId: 'cell',
          kind: 'shape',
          geometry: {
            type: 'rect',
            width: Math.max(cellWidth - 0.5, 1),
            height: Math.max(cellHeight - 0.5, 1),
            radius: 0,
          },
          style: {
            fill: lowColor,
            stroke: '#ffffff',
            strokeWidth: 0.5,
          },
          semantic: { role: 'density-cell-rect' },
        }],
      }));

      const fillMapping: Record<string, unknown> = params.colorScaleId
        ? {
            source: 'scale',
            scaleId: params.colorScaleId,
            field: params.valueField,
            fallback: lowColor,
          }
        : {
            source: 'conditional',
            field: params.valueField,
            cases: [{
              operator: 'gt',
              value: 0,
              result: highColor,
            }],
            fallback: lowColor,
          };

      // 位置映射：优先用 scale 把索引映射到像素；否则直接用字段值。
      const xMapping: Record<string, unknown> = params.xScaleId
        ? { source: 'scale', scaleId: params.xScaleId, field: params.xField, fallback: 0 }
        : { source: 'field', field: params.xField, fallback: 0 };
      const yMapping: Record<string, unknown> = params.yScaleId
        ? { source: 'scale', scaleId: params.yScaleId, field: params.yField, fallback: 0 }
        : { source: 'field', field: params.yField, fallback: 0 };

      expansion.repeats.push(sceneRepeatSchema.parse({
        id: `${invocation.id}:cells`,
        dataViewId: params.dataViewId,
        keyField: params.keyField,
        definitionId,
        parentId: rootId,
        mappings: {
          'cell.transform.x': xMapping,
          'cell.transform.y': yMapping,
          'cell-rect.style.fill': fillMapping,
        },
      }));

      if (params.showAxes) {
        // 简洁外框 + 行列刻度标签（确定性）
        expansion.nodes.push(
          rectNode(
            `${invocation.id}:frame`,
            rootId,
            params.x - 6,
            params.y - 6,
            params.width + 12,
            params.height + 12,
            'transparent',
            '',
          ),
          textNode(
            `${invocation.id}:axis-cols`,
            rootId,
            `${params.columns} 列`,
            params.x + params.width / 2,
            params.y + params.height + 18,
          ),
          textNode(
            `${invocation.id}:axis-rows`,
            rootId,
            `${params.rows} 行`,
            params.x - 10,
            params.y + params.height / 2,
          ),
        );
      }

      return expansion;
    },
  });

  const dataMappedEntitySchema = zod.object({
    dataViewId: zod.string().min(1),
    keyField: zod.string().min(1).default('id'),
    entity: zod.discriminatedUnion('kind', [
      zod.object({
        kind: zod.literal('circle'),
        radius: zod.number().positive().max(1000).default(6),
      }).strict(),
      zod.object({
        kind: zod.literal('rect'),
        width: zod.number().positive().max(100000).default(40),
        height: zod.number().positive().max(100000).default(40),
      }).strict(),
      zod.object({
        kind: zod.literal('text'),
      }).strict(),
    ]),
    xColumn: zod.string().min(1),
    yColumn: zod.string().min(1).optional(),
    fillColumn: zod.string().optional(),
    fillScaleId: zod.string().optional(),
    sizeColumn: zod.string().optional(),
    labelColumn: zod.string().optional(),
    labelOffsetY: zod.number().finite().default(14),
    parentId: zod.string().optional(),
  }).strict();
  registry.register({
    descriptor: constructDescriptor(
      'data-mapped-entity',
      '数据映射实体群',
      '用列名映射从数据生成实体群，避免手写 Definition 内部节点路径。',
      dataMappedEntitySchema,
      ['数据驱动实体', '散点', '群体', '状态着色'],
      'medium',
      [{
        dataViewId: 'entities',
        entity: { kind: 'circle', radius: 6 },
        xColumn: 'x',
        yColumn: 'y',
      }],
    ),
    schema: dataMappedEntitySchema,
    expand: (invocation, params) => {
      const expansion = emptyExpansion();
      const hasExternalParent = Boolean(invocation.parentId);
      const rootId = hasExternalParent
        ? invocation.parentId!
        : `${invocation.id}:root`;
      const definitionId = `${invocation.id}:entity-def`;
      const labelId = 'label';

      if (!hasExternalParent)
        expansion.nodes.push(groupNode(rootId, invocation.parentId));

      const glyphNodes: SceneNode[] = [];
      if (params.entity.kind === 'circle') {
        glyphNodes.push(sceneNodeSchema.parse({
          id: 'glyph',
          parentId: 'entity',
          kind: 'shape',
          geometry: { type: 'circle', radius: params.entity.radius },
          style: {
            fill: params.fillScaleId ? '#dbeafe' : '$primary',
            stroke: '#ffffff',
            strokeWidth: 1,
          },
          semantic: { role: 'mapped-entity-glyph' },
        }));
      }
      else if (params.entity.kind === 'rect') {
        glyphNodes.push(sceneNodeSchema.parse({
          id: 'glyph',
          parentId: 'entity',
          kind: 'shape',
          geometry: {
            type: 'rect',
            width: params.entity.width,
            height: params.entity.height,
            radius: 2,
          },
          style: {
            fill: params.fillScaleId ? '#dbeafe' : '$primary',
            stroke: '#ffffff',
            strokeWidth: 1,
          },
          semantic: { role: 'mapped-entity-glyph' },
        }));
      }
      else {
        glyphNodes.push(sceneNodeSchema.parse({
          id: 'glyph',
          parentId: 'entity',
          kind: 'text',
          text: '',
          transform: { x: 0, y: 0 },
          textStyle: {
            color: '$text',
            fontSize: 13,
            fontWeight: '500',
            align: 'center',
          },
          anchorX: 0.5,
          anchorY: 0.5,
          semantic: { role: 'mapped-entity-glyph' },
        }));
      }

      const definitionNodes: SceneNode[] = [
        sceneNodeSchema.parse({
          id: 'entity',
          kind: 'group',
          semantic: { role: 'mapped-entity' },
        }),
        ...glyphNodes,
      ];
      if (params.labelColumn) {
        definitionNodes.push(sceneNodeSchema.parse({
          id: labelId,
          parentId: 'entity',
          kind: 'text',
          text: '',
          transform: { x: 0, y: params.labelOffsetY },
          textStyle: {
            color: '$text',
            fontSize: 11,
            fontWeight: '500',
            align: 'center',
          },
          anchorX: 0.5,
          anchorY: 0,
          semantic: { role: 'mapped-entity-label' },
        }));
      }

      expansion.definitions.push(sceneDefinitionSchema.parse({
        id: definitionId,
        rootIds: ['entity'],
        nodes: definitionNodes,
      }));

      const mappings: Record<string, unknown> = {
        'entity.transform.x': {
          source: 'field',
          field: params.xColumn,
          fallback: 0,
        },
      };
      if (params.yColumn) {
        mappings['entity.transform.y'] = {
          source: 'field',
          field: params.yColumn,
          fallback: 0,
        };
      }
      if (params.fillColumn) {
        mappings['glyph.style.fill'] = params.fillScaleId
          ? {
              source: 'scale',
              scaleId: params.fillScaleId,
              field: params.fillColumn,
              fallback: '#2563eb',
            }
          : { source: 'field', field: params.fillColumn, fallback: '#2563eb' };
      }
      if (params.sizeColumn) {
        if (params.entity.kind === 'circle') {
          mappings['glyph.geometry.radius'] = {
            source: 'field',
            field: params.sizeColumn,
            fallback: params.entity.radius,
          };
        }
        else if (params.entity.kind === 'rect') {
          mappings['glyph.geometry.width'] = {
            source: 'field',
            field: params.sizeColumn,
            fallback: params.entity.width,
          };
        }
      }
      if (params.labelColumn) {
        mappings['label.text'] = {
          source: 'field',
          field: params.labelColumn,
          fallback: '',
        };
        mappings['entity.semantic.label'] = {
          source: 'field',
          field: params.labelColumn,
          fallback: '',
        };
      }

      expansion.repeats.push(sceneRepeatSchema.parse({
        id: `${invocation.id}:entities`,
        dataViewId: params.dataViewId,
        keyField: params.keyField,
        definitionId,
        parentId: rootId,
        mappings,
      }));

      return expansion;
    },
  });

  return registry;
}
